/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { get, post, put, del } from '@/services/api/client';
import type { ApiResponse } from '@/services/api/types';
import type { PagedResult } from '../types/product';
import type {
  RegisterListItem,
  RegisterDetail,
  RegisterStats,
  ActiveRegister,
  CreateRegisterRequest,
  UpdateRegisterRequest,
  OpenRegisterRequest,
  CloseRegisterRequest,
  ReconcileRegisterRequest,
  CashInOutRequest,
  RegisterListParams,
  RegisterVarianceDto,
  RegisterVarianceSummaryDto,
  RegisterVarianceChartDto,
  RecoverVarianceRequest,
} from '../types/cash-bank';

const BASE = '/venta/cash-registers';

export async function getRegisters(params: RegisterListParams = {}): Promise<PagedResult<RegisterListItem>> {
  const query: Record<string, unknown> = {};
  if (params.search)    query.search    = params.search;
  if (params.status)    query.status    = params.status;
  if (params.page !== undefined) query.page = params.page;
  if (params.size !== undefined) query.size = params.size;
  if (params.sort)      query.sort      = params.sort;
  if (params.direction) query.direction = params.direction;

  const response = await get<ApiResponse<PagedResult<RegisterListItem>>>(BASE, query);
  return response.data;
}

export async function getRegisterStats(): Promise<RegisterStats> {
  const response = await get<ApiResponse<RegisterStats>>(`${BASE}/stats`);
  return response.data;
}

export async function getActiveRegisters(): Promise<ActiveRegister[]> {
  const response = await get<ApiResponse<ActiveRegister[]>>(`${BASE}/active`);
  return response.data;
}

export async function getMyRegister(): Promise<ActiveRegister | null> {
  const response = await get<ApiResponse<ActiveRegister | null>>(`${BASE}/mine`);
  return response.data;
}

export async function getRegister(id: string): Promise<RegisterDetail> {
  const response = await get<ApiResponse<RegisterDetail>>(`${BASE}/${id}`);
  return response.data;
}

export async function createRegister(data: CreateRegisterRequest): Promise<RegisterListItem> {
  const response = await post<ApiResponse<RegisterListItem>>(BASE, data);
  return response.data;
}

export async function updateRegister(id: string, data: UpdateRegisterRequest): Promise<RegisterListItem> {
  const response = await put<ApiResponse<RegisterListItem>>(`${BASE}/${id}`, data);
  return response.data;
}

export async function deleteRegister(id: string): Promise<void> {
  await del<ApiResponse<void>>(`${BASE}/${id}`);
}

export async function openRegister(id: string, data: OpenRegisterRequest): Promise<RegisterDetail> {
  const response = await post<ApiResponse<RegisterDetail>>(`${BASE}/${id}/open`, data);
  return response.data;
}

export async function closeRegister(id: string, data: CloseRegisterRequest): Promise<RegisterDetail> {
  const response = await post<ApiResponse<RegisterDetail>>(`${BASE}/${id}/close`, data);
  return response.data;
}

export async function reconcileRegister(id: string, data: ReconcileRegisterRequest): Promise<RegisterDetail> {
  const response = await post<ApiResponse<RegisterDetail>>(`${BASE}/${id}/reconcile`, data);
  return response.data;
}

export async function cashIn(id: string, data: CashInOutRequest): Promise<RegisterDetail> {
  const response = await post<ApiResponse<RegisterDetail>>(`${BASE}/${id}/cash-in`, data);
  return response.data;
}

export async function cashOut(id: string, data: CashInOutRequest): Promise<RegisterDetail> {
  const response = await post<ApiResponse<RegisterDetail>>(`${BASE}/${id}/cash-out`, data);
  return response.data;
}

// ── Variance endpoints ───────────────────────────────────────────────────────

export async function getVariances(registerId: string, page = 0, size = 20): Promise<PagedResult<RegisterVarianceDto>> {
  const response = await get<ApiResponse<PagedResult<RegisterVarianceDto>>>(
    `${BASE}/${registerId}/variances`,
    { page, size },
  );
  return response.data;
}

export async function getVarianceSummary(registerId: string): Promise<RegisterVarianceSummaryDto> {
  const response = await get<ApiResponse<RegisterVarianceSummaryDto>>(`${BASE}/${registerId}/variances/summary`);
  return response.data;
}

export async function getVarianceChart(registerId: string): Promise<RegisterVarianceChartDto[]> {
  const response = await get<ApiResponse<RegisterVarianceChartDto[]>>(`${BASE}/${registerId}/variances/chart`);
  return response.data;
}

export async function resolveVariance(varianceId: string, data: RecoverVarianceRequest): Promise<RegisterVarianceDto> {
  const response = await post<ApiResponse<RegisterVarianceDto>>(`${BASE}/variances/${varianceId}/resolve`, data);
  return response.data;
}

export async function clearDepositVariance(varianceId: string, data: { amount: number; note: string }): Promise<RegisterVarianceDto> {
  const response = await post<ApiResponse<RegisterVarianceDto>>(`${BASE}/variances/${varianceId}/clear-deposit`, data);
  return response.data;
}
